/**
 * Hash Map utility functions for O(1) lookups
 * These utilities help replace O(n) array.find() operations with O(1) Map lookups
 */

import { createBenchmarkedMap } from './performanceMonitor';

/**
 * Create a Map from an array using a single ID/key
 * @param items - Array of items to map
 * @param getId - Function to extract the ID from an item
 * @param operation - Optional operation name for performance monitoring
 * @returns Map with ID as key and item as value
 *
 * @example
 * const providerMap = createIdMap(providers, p => p.id);
 * const provider = providerMap.get('some-id'); // O(1) lookup
 */
export function createIdMap<T>(
  items: T[],
  getId: (item: T) => string | number | null | undefined,
  operation?: string
): Map<string, T> {
  if (operation) {
    // createBenchmarkedMap expects getId to return string | number, so we filter out null/undefined
    const filteredItems = items.filter(item => {
      const id = getId(item);
      return id !== null && id !== undefined;
    });
    return createBenchmarkedMap(filteredItems, (item: T) => getId(item) as string | number, operation);
  }

  const map = new Map<string, T>();
  for (const item of items) {
    const id = getId(item);
    if (id !== null && id !== undefined) {
      map.set(String(id), item);
    }
  }
  return map;
}

/**
 * Create a Map where each item can be accessed by multiple keys
 * @param items - Array of items to map
 * @param getKeys - Function that returns an array of keys for an item
 * @returns Map with each key mapping to an array of items
 *
 * @example
 * const clinicMap = createMultiKeyMap(clinics, c => [c.id, c.legacy_id].filter(Boolean));
 * const clinics = clinicMap.get('some-id'); // Returns array of clinics with that key
 */
export function createMultiKeyMap<T>(
  items: T[],
  getKeys: (item: T) => (string | number | null | undefined)[]
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const keys = getKeys(item);
    for (const key of keys) {
      if (key !== null && key !== undefined) {
        const keyStr = String(key);
        const existing = map.get(keyStr) ?? [];
        existing.push(item);
        map.set(keyStr, existing);
      }
    }
  }
  return map;
}

/**
 * Create a Map with composite keys (e.g., "id:legacy_id")
 * Useful when you need to look up items by multiple possible identifiers
 * @param items - Array of items to map
 * @param getKey - Function that returns a composite key string
 * @returns Map with composite key as key and item as value
 *
 * @example
 * const clinicMap = createCompositeKeyMap(clinics, c => `${c.id}:${c.legacy_id}`);
 */
export function createCompositeKeyMap<T>(items: T[], getKey: (item: T) => string): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    const key = getKey(item);
    if (key) {
      map.set(key, item);
    }
  }
  return map;
}

/**
 * Create a Map from an array with support for multiple ID fields (id, legacy_id, etc.)
 * This is useful for entities that have both id and legacy_id fields
 * @param items - Array of items to map
 * @param getIdFields - Function that returns an object with id fields
 * @returns Map where items can be looked up by any of their ID fields
 *
 * @example
 * const clinicMap = createMultiIdMap(clinics, c => ({ id: c.id, legacyId: c.legacy_id }));
 * const clinic1 = clinicMap.get('some-id'); // Lookup by id
 * const clinic2 = clinicMap.get('some-legacy-id'); // Lookup by legacy_id
 */
export function createMultiIdMap<T>(
  items: T[],
  getIdFields: (item: T) => Record<string, string | number | null | undefined>
): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    const idFields = getIdFields(item);
    for (const [, idValue] of Object.entries(idFields)) {
      if (idValue !== null && idValue !== undefined) {
        map.set(String(idValue), item);
      }
    }
  }
  return map;
}
